import type { DashboardStats } from "../types";

interface PalletCostTableProps {
  stats: DashboardStats;
}

interface PivotedRow {
  pallet_type: string;
  economy_avg: number;
  economy_count: number;
  premium_avg: number;
  premium_count: number;
}

function pivotCosts(stats: DashboardStats): PivotedRow[] {
  const costs = stats.average_pallet_costs;
  if (!costs || costs.length === 0) return [];

  // Group by pallet_type, pivot band A/B into columns
  const map = new Map<string, PivotedRow>();

  for (const entry of costs) {
    if (!map.has(entry.pallet_type)) {
      map.set(entry.pallet_type, {
        pallet_type: entry.pallet_type,
        economy_avg: 0,
        economy_count: 0,
        premium_avg: 0,
        premium_count: 0,
      });
    }
    const row = map.get(entry.pallet_type)!;
    if (entry.band === "A") {
      row.economy_avg = entry.average_cost;
      row.economy_count = entry.count;
    } else {
      row.premium_avg = entry.average_cost;
      row.premium_count = entry.count;
    }
  }

  // Sort: FP, LP, ELP, HP, QP, MQP
  const order = ["FP", "LP", "ELP", "HP", "QP", "MQP"];
  return [...map.values()].sort(
    (a, b) => (order.indexOf(a.pallet_type) ?? 99) - (order.indexOf(b.pallet_type) ?? 99),
  );
}

const formatCurrency = (n: number) =>
  `\u00A3${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PalletCostTable({ stats }: PalletCostTableProps) {
  const rows = pivotCosts(stats);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">
          Average Pallet Cost
        </h2>
        <p className="mt-3 text-sm text-gray-500">
          No pallet cost data available yet. Process some haulier invoices to
          see averages here.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Average Pallet Cost
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-6 py-3">Pallet Type</th>
              <th className="px-6 py-3 text-right">Economy Avg</th>
              <th className="px-6 py-3 text-right">Economy Count</th>
              <th className="px-6 py-3 text-right">Premium Avg</th>
              <th className="px-6 py-3 text-right">Premium Count</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={row.pallet_type} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-6 py-3 font-medium text-gray-900">
                  {row.pallet_type}
                </td>
                <td className="whitespace-nowrap px-6 py-3 text-right font-mono text-gray-700">
                  {row.economy_count > 0 ? formatCurrency(row.economy_avg) : "\u2014"}
                </td>
                <td className="whitespace-nowrap px-6 py-3 text-right text-gray-500">
                  {row.economy_count > 0 ? row.economy_count.toLocaleString() : "\u2014"}
                </td>
                <td className="whitespace-nowrap px-6 py-3 text-right font-mono text-gray-700">
                  {row.premium_count > 0 ? formatCurrency(row.premium_avg) : "\u2014"}
                </td>
                <td className="whitespace-nowrap px-6 py-3 text-right text-gray-500">
                  {row.premium_count > 0 ? row.premium_count.toLocaleString() : "\u2014"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
